import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from '@wordpress/element';
import { usePricingCalculation } from './usePricingCalculation';
import { useFormFieldDetection } from './useFormFieldDetection';
import { useBookingStore } from '../store/useBookingStore';
import { numberFormat } from '../../helpers/currency';
import {
	registerBookingProductFilter,
	refreshGFProducts,
} from '../utils/gf-products';

/**
 * Blocks the controls that end the current page while a price is being
 * calculated. Submit is not the only one: on a multi-page form the button the
 * user is about to press is Next, and pressing it mid-calculation posts the
 * price as it stands and carries a stale total to the following page.
 */
function setNavigationDisabled(formId: number, disabled: boolean): void {
	const buttons = document.querySelectorAll<
		HTMLButtonElement | HTMLInputElement
	>(`#gform_submit_button_${formId}, #gform_${formId} .gform_next_button`);

	buttons.forEach((button) => {
		button.disabled = disabled;
		button.style.cursor = disabled ? 'wait' : '';
	});
}

/**
 * Outstanding price calculations per form, across every Booking field on it.
 *
 * The lock has to be counted per FORM rather than per field: a form may hold
 * several Booking fields, each with its own calculations, but all of them share
 * the one Next button. Counting per field lets whichever field finishes first
 * re-enable navigation while another booking's price is still in flight, which
 * is the stale-total window this lock exists to close.
 */
const pendingByForm = new Map<number, number>();

function lockNavigation(formId: number): void {
	const pending = (pendingByForm.get(formId) ?? 0) + 1;

	pendingByForm.set(formId, pending);

	if (pending === 1) {
		setNavigationDisabled(formId, true);
	}
}

function unlockNavigation(formId: number): void {
	const pending = Math.max(0, (pendingByForm.get(formId) ?? 0) - 1);

	pendingByForm.set(formId, pending);

	if (pending === 0) {
		setNavigationDisabled(formId, false);
	}
}

interface GravityFormsPricingOptions {
	formId: number;
	bookingFieldId: number;
	serviceId: number;
	resourceFieldIds: number[];
}

export function useGravityFormsPricing({
	formId,
	bookingFieldId,
	serviceId,
	resourceFieldIds,
}: GravityFormsPricingOptions) {
	const [isCalculating, setIsCalculating] = useState(false);

	// Calculations can overlap (rapid occupancy changes queue several). Only
	// the last one to finish may re-enable the submit button.
	const pendingCalculations = useRef(0);

	const { calculatePrice, clearCache } = usePricingCalculation({
		formId,
		serviceId,
		bookingFieldId,
	});

	const { detectFormFields, getPriceInput } = useFormFieldDetection({
		formId,
		bookingFieldId,
		resourceFieldIds,
	});

	// Read from store instead of individual hooks
	const resourceIds = useBookingStore((state) => state.selectedResourceIds);
	const quantity = useBookingStore((state) => state.quantity);
	const occupancy = useBookingStore((state) => state.occupancy);
	const isInitialized = useBookingStore((state) => state.isInitialized);

	// Recalculate the total on mapped Option changes. GF versions before 3.0
	// don't recalculate for our custom inputType, so their own recalc doesn't fire.
	useEffect(() => {
		// Teach GF's product state about the booking field so its price survives
		// a recalculation triggered by any other product field on the form.
		registerBookingProductFilter();

		const form = document.getElementById(`gform_${formId}`);
		if (!form) {
			return;
		}
		const optionInputs = form.querySelectorAll(
			`.gfield_option_${formId}_${bookingFieldId} input, .gfield_option_${formId}_${bookingFieldId} select`
		);
		const handler = () => refreshGFProducts(formId);
		optionInputs.forEach((el) => el.addEventListener('change', handler));
		return () => {
			optionInputs.forEach((el) =>
				el.removeEventListener('change', handler)
			);
		};
	}, [formId, bookingFieldId]);

	const updatePricing = useCallback(
		async (bookingFieldId: number, timeValue?: string): Promise<void> => {
			// Use proper GF types from @gravitywiz/types
			if (!window.gform?.applyFilters) {
				// Fallback for older GF versions
				return;
			}

			// Parse time value
			let startTime: string | undefined;
			let endTime: string | undefined;

			if (timeValue) {
				if (timeValue.includes('|')) {
					[startTime, endTime] = timeValue.split('|');
				} else {
					startTime = timeValue;
					endTime = timeValue;
				}
			}

			// Disable the buttons that end the page and show a loading cursor
			pendingCalculations.current += 1;
			setIsCalculating(true);
			lockNavigation(formId);

			try {
				const calculatedPrice = await calculatePrice({
					resource_ids: resourceIds,
					start_time: startTime,
					end_time: endTime,
					quantity,
					occupancy: occupancy ?? undefined,
					field_id: bookingFieldId,
				});

				// Superseded or failed, so leave the displayed total alone.
				if (calculatedPrice === null) {
					return;
				}

				// Backend returns the per-unit price (calculated with quantity=1).
				// GF multiplies it by the quantity to get the product total, so
				// the price input is the only thing that needs updating here.
				const priceInput = getPriceInput(bookingFieldId);
				if (priceInput && !priceInput.dataset.updating) {
					priceInput.dataset.updating = 'true';
					priceInput.value = numberFormat(calculatedPrice);
					priceInput.dispatchEvent(
						new Event('change', { bubbles: true })
					);

					// Clean up the updating flag after a microtask
					Promise.resolve().then(() => {
						delete priceInput.dataset.updating;
					});
				}

				// Force GF to recalculate totals
				refreshGFProducts(formId);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Failed to update booking pricing:', error);
			} finally {
				pendingCalculations.current -= 1;

				// This field is done calculating once its own count clears; the
				// buttons only come back when no Booking field on the form is
				// still calculating.
				if (pendingCalculations.current === 0) {
					setIsCalculating(false);
				}

				unlockNavigation(formId);
			}
		},
		[
			formId,
			calculatePrice,
			resourceIds,
			quantity,
			occupancy,
			getPriceInput,
		]
	);

	// Trigger pricing update when resourceIds, quantity, or occupancy change
	// Only run after initialization to prevent premature pricing calculations
	useLayoutEffect(() => {
		if (isInitialized && resourceIds.length > 0) {
			// Clear cache when resources, quantity, or occupancy change
			clearCache();

			// Get time field value for pricing calculation
			const { timeFieldElement } = detectFormFields();
			updatePricing(bookingFieldId, timeFieldElement?.value);
		}
	}, [
		isInitialized,
		resourceIds,
		quantity,
		occupancy,
		bookingFieldId,
		updatePricing,
		clearCache,
		detectFormFields,
	]);

	return {
		updatePricing,
		clearCache,
		isCalculating,
	};
}
